import { PaymentClass, PaymentStatus } from 'generated/graphql';
import {
	buildEcrVoidRequest,
	CardBizEcrRecord,
} from 'helpers/offlineTerminal/getOutletEcrTerminal';
import {
	decodeEcrPaymentMeta,
	isEcrPaymentRecord,
} from 'helpers/offlineTerminal/ecrPaymentStorage';
import { formatEcrResponseMessage, isEcrAlreadyVoidedMessage } from 'helpers/offlineTerminal/formatEcrResponseMessage';
import { isOfflineTerminalActive } from 'helpers/offlineTerminal/offlineTerminalConfig';
import { voidOnTerminal } from 'services/ecrAgent';

type PaymentRow = {
	ID?: string;
	paymentClass?: string;
	status?: string;
	referenceNo?: string | null;
	checkoutID?: string | null;
	amount?: number;
	cardNo?: string | null;
	creditCardType?: string | null;
};

function isCreditPayment(payment: PaymentRow): boolean {
	return (
		String(payment?.paymentClass || '').toUpperCase() === PaymentClass.Credit
	);
}

/** Card payment taken on N950S (not hotel credit account). */
function isPhysicalCardPayment(payment: PaymentRow): boolean {
	return isCreditPayment(payment) && Boolean(payment.cardNo || payment.creditCardType);
}

/** Payments that must be voided on the N950S before change payment continues. */
export function paymentsRequiringTerminalVoid(
	payments: PaymentRow[],
): PaymentRow[] {
	return payments.filter(payment => {
		if (!isCreditPayment(payment)) {
			return false;
		}
		if (isEcrPaymentRecord(payment.checkoutID)) {
			return true;
		}
		return isPhysicalCardPayment(payment);
	});
}

/**
 * Open bill payments that need a CardBiz void on the terminal.
 * Void bill: all active credit/ECR payments on the bill.
 * Change payment: only the selected payment(s) being replaced.
 */
export function resolvePaymentsForTerminalVoid(
	allPayments: PaymentRow[] | null | undefined,
	options: {
		voidWholeBill?: boolean;
		selectedPaymentIds?: string[];
	},
): PaymentRow[] {
	const openPayments = (allPayments ?? []).filter(
		p => p?.status === PaymentStatus.Open,
	);

	const scoped = options.voidWholeBill
		? openPayments
		: openPayments.filter(p =>
				(options.selectedPaymentIds ?? []).includes(p.ID ?? ''),
		  );

	return paymentsRequiringTerminalVoid(scoped);
}

/**
 * CardBiz void (spec §7.12) requires field 65 — invoice/trace from the original sale.
 * Stored in payment.checkoutID as cardbiz-ecr:{"inv":"000009",...}
 */
function resolveCardBizInvoiceNumber(payment: PaymentRow): string | undefined {
	const ecrMeta = decodeEcrPaymentMeta(payment.checkoutID);
	const invoice = ecrMeta?.invoiceNumber?.trim();
	if (invoice && /^\d{1,6}$/.test(invoice)) {
		return invoice.padStart(6, '0');
	}
	return undefined;
}

function resolveCardBizApprovalCode(payment: PaymentRow): string | undefined {
	const ecrMeta = decodeEcrPaymentMeta(payment.checkoutID);
	return ecrMeta?.approvalCode?.trim() || payment.referenceNo?.trim() || undefined;
}

/**
 * Before voiding credit payments in MenuX, void them on the terminal first (CardBiz code 40).
 * Returns an error message if any terminal void fails; null if all OK or N/A.
 */
export async function voidCreditPaymentsOnTerminal(
	payments: PaymentRow[],
	transactionId: string,
	outletId: string,
	cardBizEcrList?: CardBizEcrRecord[] | null,
): Promise<string | null> {
	if (!isOfflineTerminalActive(cardBizEcrList)) {
		return null;
	}

	if (!transactionId?.trim()) {
		return 'Cannot void on terminal — missing order number (transaction ID).';
	}

	const terminalTargets = paymentsRequiringTerminalVoid(payments);
	if (terminalTargets.length === 0) {
		return null;
	}

	let performedVoid = false;

	for (const payment of terminalTargets) {
		const invoiceNumber = resolveCardBizInvoiceNumber(payment);
		const approvalCode = resolveCardBizApprovalCode(payment);

		if (!invoiceNumber) {
			return (
				'Cannot void on terminal — missing CardBiz invoice number (field 65 from the original sale). ' +
				'Re-pay with the latest MenuX so checkoutID is saved, or void manually on the N950S ' +
				`(Bill → Change Payment → use invoice from sale receipt, order ${transactionId}).`
			);
		}

		const result = await voidOnTerminal(
			buildEcrVoidRequest({
				transactionId,
				outletId,
				amount: Number(payment.amount ?? 0),
				invoiceNumber,
				approvalCode,
				cardBizEcrList,
			}),
		);

		if (!result.approved) {
			if (isEcrAlreadyVoidedMessage(result.responseMessage)) {
				performedVoid = true;
				continue;
			}

			return (
				formatEcrResponseMessage(result.responseMessage) ||
				`Terminal void failed for invoice ${invoiceNumber}. Payment was not voided in MenuX.`
			);
		}

		performedVoid = true;
	}

	if (!performedVoid) {
		return (
			'Cannot void on terminal — no ECR card payment was voided. ' +
			'Ensure checkoutID is saved on the original payment.'
		);
	}

	return null;
}
